/**
 * WMM (World Magnetic Model) 2025 — magnetic declination only (spec 14C).
 *
 * The coefficient table below is transcribed verbatim from the 90 data rows
 * of `WMM2025.COF` (NOAA/NCEI, public domain, epoch 2025.0). That file and
 * `WMM2025_TEST_VALUES.txt` (NOAA's official acceptance table) live alongside
 * this module as provenance + the `geomag.test.ts` fixture.
 *
 * Validity window: 2025.0–2030.0 (NOAA's 5-year WMM release cycle). Refresh
 * for the next model by swapping in the new .COF file and re-transcribing
 * COF_DATA below — same algorithm, new numbers.
 *
 * Algorithm (standard WMM spherical-harmonic synthesis to degree/order 12):
 *  1. Geodetic (WGS84) → geocentric spherical conversion.
 *  2. Schmidt semi-normalized associated Legendre functions + derivatives
 *     (Gauss-normalized recursion, then rescaled).
 *  3. Secular variation: coefficient(t) = coefficient(2025.0) + (t−2025.0)·dot.
 *  4. Spherical-harmonic summation → geocentric field (Bx north, By east,
 *     Bz down), then rotated back to the geodetic frame.
 *  5. Declination D = atan2(By, Bx).
 *
 * Only declination is exposed publicly (`declinationDeg`). `fieldAt` is
 * exported too, but only so `geomag.test.ts` can validate against every row
 * of NOAA's test-value table, including its non-zero ellipsoidal heights —
 * `declinationDeg` itself always uses height 0 (sea level), the only case
 * this app needs.
 */

const N_MAX = 12;
const EPOCH = 2025.0;

// WGS84 ellipsoid, kilometres.
const WGS84_A = 6378.137;
const WGS84_B = 6356.7523142;
const WGS84_EPS_SQ = (WGS84_A * WGS84_A - WGS84_B * WGS84_B) / (WGS84_A * WGS84_A);

// WMM geomagnetic reference radius (km) — distinct from the WGS84 ellipsoid.
const RE = 6371.2;

const DEG2RAD = Math.PI / 180;
const RAD2DEG = 180 / Math.PI;

/** Triangular (n,m) index into a flat degree/order-12 coefficient array. */
function idx(n: number, m: number): number {
  return (n * (n + 1)) / 2 + m;
}

const TABLE_SIZE = idx(N_MAX, N_MAX) + 1;

/**
 * n m g h gdot hdot — verbatim from WMM2025.COF's 90 data rows (n=1..12).
 * nT and nT/year respectively.
 */
const COF_DATA = `
 1  0  -29351.8       0.0       12.0        0.0
 1  1   -1410.8    4545.4        9.7      -21.5
 2  0   -2556.6       0.0      -11.6        0.0
 2  1    2951.1   -3133.6       -5.2      -27.7
 2  2    1649.3    -815.1       -8.0      -12.1
 3  0    1361.0       0.0       -1.3        0.0
 3  1   -2404.1     -56.6       -4.2        4.0
 3  2    1243.8     237.5        0.4       -0.3
 3  3     453.6    -549.5      -15.6       -4.1
 4  0     895.0       0.0       -1.6        0.0
 4  1     799.5     278.6       -2.4       -1.1
 4  2      55.7    -133.9       -6.0        4.1
 4  3    -281.1     212.0        5.6        1.6
 4  4      12.1    -375.6       -7.0       -4.4
 5  0    -233.2       0.0        0.6        0.0
 5  1     368.9      45.4        1.4       -0.5
 5  2     187.2     220.2        0.0        2.2
 5  3    -138.7    -122.9        0.6        0.4
 5  4    -142.0      43.0        2.2        1.7
 5  5      20.9     106.1        0.9        1.9
 6  0      64.4       0.0       -0.2        0.0
 6  1      63.8     -18.4       -0.4        0.3
 6  2      76.9      16.8        0.9       -1.6
 6  3    -115.7      48.8        1.2       -0.4
 6  4     -40.9     -59.8       -0.9        0.9
 6  5      14.9      10.9        0.3        0.7
 6  6     -60.7      72.7        0.9        0.9
 7  0      79.5       0.0       -0.0        0.0
 7  1     -77.0     -48.9       -0.1        0.6
 7  2      -8.8     -14.4       -0.1        0.5
 7  3      59.3      -1.0        0.5       -0.8
 7  4      15.8      23.4       -0.1        0.0
 7  5       2.5      -7.4       -0.8       -1.0
 7  6     -11.1     -25.1       -0.8        0.6
 7  7      14.2      -2.3        0.8       -0.2
 8  0      23.2       0.0       -0.1        0.0
 8  1      10.8       7.1        0.2       -0.2
 8  2     -17.5     -12.6        0.0        0.5
 8  3       2.0      11.4        0.5       -0.4
 8  4     -21.7      -9.7       -0.1        0.4
 8  5      16.9      12.7        0.3       -0.5
 8  6      15.0       0.7        0.2       -0.6
 8  7     -16.8      -5.2       -0.0        0.3
 8  8       0.9       3.9        0.2        0.2
 9  0       4.6       0.0       -0.0        0.0
 9  1       7.8     -24.8       -0.1       -0.3
 9  2       3.0      12.2        0.1        0.3
 9  3      -0.2       8.3        0.3       -0.3
 9  4      -2.5      -3.3       -0.3        0.3
 9  5     -13.1      -5.2        0.0        0.2
 9  6       2.4       7.2        0.3       -0.1
 9  7       8.6      -0.6       -0.1       -0.2
 9  8      -8.7       0.8        0.1        0.4
 9  9     -12.9      10.0       -0.1        0.1
10  0      -1.3       0.0        0.1        0.0
10  1      -6.4       3.3        0.0        0.0
10  2       0.2       0.0        0.1       -0.0
10  3       2.0       2.4        0.1       -0.2
10  4      -1.0       5.3       -0.0        0.1
10  5      -0.6      -9.1       -0.3       -0.1
10  6      -0.9       0.4        0.0        0.1
10  7       1.5      -4.2       -0.1        0.0
10  8       0.9      -3.8       -0.1       -0.1
10  9      -2.7       0.9       -0.0        0.2
10 10      -3.9      -9.1       -0.0       -0.0
11  0       2.9       0.0        0.0        0.0
11  1      -1.5       0.0       -0.0       -0.0
11  2      -2.5       2.9        0.0        0.1
11  3       2.4      -0.6        0.0       -0.0
11  4      -0.6       0.2        0.0        0.1
11  5      -0.1       0.5       -0.1       -0.0
11  6      -0.6      -0.3        0.0       -0.0
11  7      -0.1      -1.2       -0.0        0.1
11  8       1.1      -1.7       -0.1       -0.0
11  9      -1.0      -2.9       -0.1        0.0
11 10      -0.2      -1.8       -0.1        0.0
11 11       2.6      -2.3       -0.1        0.0
12  0      -2.0       0.0        0.0        0.0
12  1      -0.2      -1.3        0.0       -0.0
12  2       0.3       0.7       -0.0        0.0
12  3       1.2       1.0       -0.0       -0.1
12  4      -1.3      -1.4       -0.0        0.1
12  5       0.6      -0.0       -0.0       -0.0
12  6       0.6       0.6        0.1       -0.0
12  7       0.5      -0.1       -0.0       -0.0
12  8      -0.1       0.8        0.0        0.0
12  9      -0.4       0.1        0.0       -0.0
12 10      -0.2      -1.0       -0.1       -0.0
12 11      -1.3       0.1       -0.0        0.0
12 12      -0.7       0.2       -0.1       -0.1
`;

interface CoefTable {
  g0: Float64Array;
  h0: Float64Array;
  gdot: Float64Array;
  hdot: Float64Array;
}

function parseCoefficients(raw: string): CoefTable {
  const g0 = new Float64Array(TABLE_SIZE);
  const h0 = new Float64Array(TABLE_SIZE);
  const gdot = new Float64Array(TABLE_SIZE);
  const hdot = new Float64Array(TABLE_SIZE);
  for (const line of raw.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const parts = trimmed.split(/\s+/).map(Number);
    const [n, m, g, h, gd, hd] = parts;
    if (n === undefined || m === undefined) continue;
    const i = idx(n, m);
    g0[i] = g ?? 0;
    h0[i] = h ?? 0;
    gdot[i] = gd ?? 0;
    hdot[i] = hd ?? 0;
  }
  return { g0, h0, gdot, hdot };
}

const COEFS = parseCoefficients(COF_DATA);

export interface FieldResult {
  /** Magnetic declination, degrees, positive East. */
  declinationDeg: number;
  /** North component, geodetic frame, nT. */
  bxNt: number;
  /** East component, nT. */
  byNt: number;
  /** Down component, geodetic frame, nT. */
  bzNt: number;
}

/**
 * Full WMM field evaluation at a geodetic position/height/epoch. Internal —
 * `declinationDeg` below is the public surface — but exported so the test
 * suite can exercise NOAA's non-zero-height test rows too.
 */
export function fieldAt(
  latDeg: number,
  lonDeg: number,
  heightKm: number,
  decimalYear: number,
): FieldResult {
  // --- 1. geodetic (WGS84) -> geocentric spherical ---
  const latRad = latDeg * DEG2RAD;
  const cosLat = Math.cos(latRad);
  const sinLat = Math.sin(latRad);
  const rc = WGS84_A / Math.sqrt(1 - WGS84_EPS_SQ * sinLat * sinLat);
  const xp = (rc + heightKm) * cosLat;
  const zp = (rc * (1 - WGS84_EPS_SQ) + heightKm) * sinLat;
  const r = Math.sqrt(xp * xp + zp * zp);
  const geocentricLatRad = Math.asin(zp / r);
  const lonRad = lonDeg * DEG2RAD;

  // --- 2. Schmidt semi-normalized associated Legendre functions + derivatives ---
  const x = Math.sin(geocentricLatRad);
  const z = Math.sqrt(Math.max(0, (1 - x) * (1 + x)));
  const P = new Float64Array(TABLE_SIZE);
  const dP = new Float64Array(TABLE_SIZE);
  P[0] = 1;
  dP[0] = 0;

  for (let n = 1; n <= N_MAX; n++) {
    for (let m = 0; m <= n; m++) {
      const index = idx(n, m);
      if (n === m) {
        const i1 = idx(n - 1, m - 1);
        P[index] = z * P[i1]!;
        dP[index] = z * dP[i1]! + x * P[i1]!;
      } else if (n === 1 && m === 0) {
        const i1 = idx(0, 0);
        P[index] = x * P[i1]!;
        dP[index] = x * dP[i1]! - z * P[i1]!;
      } else {
        const i2 = idx(n - 1, m);
        if (m > n - 2) {
          P[index] = x * P[i2]!;
          dP[index] = x * dP[i2]! - z * P[i2]!;
        } else {
          const i1 = idx(n - 2, m);
          const k = ((n - 1) * (n - 1) - m * m) / ((2 * n - 1) * (2 * n - 3));
          P[index] = x * P[i2]! - k * P[i1]!;
          dP[index] = x * dP[i2]! - z * P[i2]! - k * dP[i1]!;
        }
      }
    }
  }

  // Gauss-normalized -> Schmidt quasi-normalized rescale factors.
  const S = new Float64Array(TABLE_SIZE);
  S[0] = 1;
  for (let n = 1; n <= N_MAX; n++) {
    S[idx(n, 0)] = (S[idx(n - 1, 0)]! * (2 * n - 1)) / n;
    for (let m = 1; m <= n; m++) {
      S[idx(n, m)] = S[idx(n, m - 1)]! * Math.sqrt(((n - m + 1) * (m === 1 ? 2 : 1)) / (n + m));
    }
  }
  for (let n = 1; n <= N_MAX; n++) {
    for (let m = 0; m <= n; m++) {
      const index = idx(n, m);
      P[index] = P[index]! * S[index]!;
      // Sign flip: WMM's dPcup is d/d(latitude), not d/d(colatitude).
      dP[index] = -dP[index]! * S[index]!;
    }
  }

  // --- 3. secular variation ---
  const dt = decimalYear - EPOCH;

  // --- 4. spherical-harmonic summation (geocentric frame) ---
  const cosLon = new Float64Array(N_MAX + 1);
  const sinLon = new Float64Array(N_MAX + 1);
  cosLon[0] = 1;
  sinLon[0] = 0;
  cosLon[1] = Math.cos(lonRad);
  sinLon[1] = Math.sin(lonRad);
  for (let m = 2; m <= N_MAX; m++) {
    cosLon[m] = cosLon[m - 1]! * cosLon[1]! - sinLon[m - 1]! * sinLon[1]!;
    sinLon[m] = sinLon[m - 1]! * cosLon[1]! + cosLon[m - 1]! * sinLon[1]!;
  }

  let bx = 0;
  let by = 0;
  let bz = 0;
  for (let n = 1; n <= N_MAX; n++) {
    const relRadius = Math.pow(RE / r, n + 2);
    for (let m = 0; m <= n; m++) {
      const index = idx(n, m);
      const g = COEFS.g0[index]! + dt * COEFS.gdot[index]!;
      const h = COEFS.h0[index]! + dt * COEFS.hdot[index]!;
      const term = g * cosLon[m]! + h * sinLon[m]!;
      bz -= relRadius * term * (n + 1) * P[index]!;
      by += relRadius * (g * sinLon[m]! - h * cosLon[m]!) * m * P[index]!;
      bx -= relRadius * term * dP[index]!;
    }
  }
  const cosGeocentricLat = Math.cos(geocentricLatRad);
  if (Math.abs(cosGeocentricLat) > 1e-10) {
    by = by / cosGeocentricLat;
  }
  // Pole singularity (By/cosφ blow-up) intentionally unhandled: not a
  // navigation case for a Norway coastal app.

  // --- 5. rotate geocentric field back to the geodetic frame ---
  const psi = geocentricLatRad - latRad;
  const bxGeo = bx * Math.cos(psi) - bz * Math.sin(psi);
  const bzGeo = bx * Math.sin(psi) + bz * Math.cos(psi);

  return {
    declinationDeg: Math.atan2(by, bxGeo) * RAD2DEG,
    bxNt: bxGeo,
    byNt: by,
    bzNt: bzGeo,
  };
}

export type WmmDate = Date | number;

/** A `Date` → decimal year (UTC); a plain number is already a decimal year. */
function toDecimalYear(date: WmmDate): number {
  if (typeof date === 'number') return date;
  const year = date.getUTCFullYear();
  const start = Date.UTC(year, 0, 1);
  const end = Date.UTC(year + 1, 0, 1);
  return year + (date.getTime() - start) / (end - start);
}

/**
 * Magnetic declination (degrees, positive East) at a geodetic position and
 * date, per WMM2025. `date` accepts a `Date` or a decimal year number
 * (e.g. `2025.0`). `heightKm` defaults to 0 (sea level) — the only case this
 * app needs; it's exposed only so `geomag.test.ts` can validate NOAA's
 * non-zero-height rows too.
 */
export function declinationDeg(
  lat: number,
  lon: number,
  date: WmmDate,
  heightKm = 0,
): number {
  return fieldAt(lat, lon, heightKm, toDecimalYear(date)).declinationDeg;
}
